import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Loader2, Trophy, Newspaper, ChevronDown, ExternalLink, MapPin, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navigation from '../components/Navigation';
import TooltipGuide from '../components/TooltipGuide';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOURS = [
  { value: 'pga', label: 'PGA Tour' },
  { value: 'lpga', label: 'LPGA Tour' },
  { value: 'dpwt', label: 'DP World Tour' },
];

const GolfUpdates = () => {
  const [tour, setTour] = useState('pga');
  const [tab, setTab] = useState('leaderboard');
  const [scoreboard, setScoreboard] = useState(null);
  const [news, setNews] = useState(null);
  const [loadingLB, setLoadingLB] = useState(false);
  const [loadingNews, setLoadingNews] = useState(false);
  const [showTourPicker, setShowTourPicker] = useState(false);

  useEffect(() => {
    fetchScoreboard(tour);
    fetchNews(tour);
  }, [tour]);

  const fetchScoreboard = async (t) => {
    setLoadingLB(true);
    try {
      const res = await axios.get(`${API}/golfupdates/scoreboard?tour=${t}`);
      setScoreboard(res.data);
    } catch { setScoreboard({ tour: t, tournament: null, leaderboard: [], message: 'No tournament data available for this tour right now.' }); }
    finally { setLoadingLB(false); }
  };

  const fetchNews = async (t) => {
    setLoadingNews(true);
    try {
      const res = await axios.get(`${API}/golfupdates/news?tour=${t}`);
      setNews(res.data);
    } catch { setNews(null); }
    finally { setLoadingNews(false); }
  };

  const formatScore = (score) => {
    if (!score || score === '--' || score === 'E') return 'E';
    const n = parseInt(score);
    if (isNaN(n)) return score;
    return n > 0 ? `+${n}` : `${n}`;
  };

  const scoreColor = (score) => {
    if (!score || score === '--' || score === 'E' || score === '0') return 'text-foreground';
    const n = parseInt(score);
    if (isNaN(n)) return 'text-foreground';
    return n < 0 ? 'text-red-500' : n > 0 ? 'text-blue-500' : 'text-foreground';
  };

  const tourLabel = TOURS.find(t => t.value === tour)?.label || 'PGA Tour';

  const renderTournamentHeader = () => {
    const t = scoreboard?.tournament;
    if (!t) return null;
    return (
      <div className="bg-card rounded-2xl border border-border/50 p-4 mb-4" data-testid="tournament-header">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-bold text-foreground leading-tight">{t.name}</h2>
            {t.venue && (
              <div className="flex items-center gap-1.5 mt-1.5 text-xs text-muted-foreground">
                <MapPin className="w-3 h-3 flex-shrink-0" />
                <span>{t.venue}{t.location ? ` · ${t.location}` : ''}</span>
              </div>
            )}
          </div>
        </div>
        <div className="mt-2.5 flex items-center gap-2 flex-wrap">
          <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${
            t.status_state === 'in' ? 'bg-emerald-500/15 text-emerald-600 border border-emerald-500/20' :
            t.status_state === 'post' ? 'bg-muted text-muted-foreground border border-border/30' :
            'bg-primary/10 text-primary border border-primary/20'
          }`} data-testid="tournament-status">
            {t.status_state === 'in' && <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1.5 animate-pulse" />}
            {t.status}
          </span>
        </div>
      </div>
    );
  };

  const renderLeaderboard = () => {
    if (loadingLB) return <div className="flex items-center justify-center py-12 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading leaderboard...</div>;
    if (!scoreboard?.leaderboard?.length) return <div className="text-center py-12 text-muted-foreground text-sm">No leaderboard data available</div>;

    const lb = scoreboard.leaderboard;
    const maxRounds = Math.max(...lb.map(p => p.rounds?.length || 0), 0);

    return (
      <div className="space-y-0">
        {/* Column headers */}
        <div className="flex items-center px-3 py-2 text-[9px] font-bold text-muted-foreground uppercase tracking-wider border-b border-border/30" data-testid="leaderboard-header">
          <span className="w-8 text-center">Pos</span>
          <span className="flex-1 ml-2">Player</span>
          <span className="w-12 text-center">Total</span>
          <span className="w-10 text-center">Thru</span>
          {Array.from({ length: maxRounds }, (_, i) => (
            <span key={i} className="w-8 text-center hidden sm:block">R{i + 1}</span>
          ))}
        </div>

        {lb.map((player, i) => {
          const isCut = player.status === 'STATUS_CUT';
          const isWD = player.status === 'STATUS_WITHDRAWN';
          const isTop3 = player.position <= 3 && !isCut && !isWD;
          return (
            <div
              key={`${player.name}-${i}`}
              className={`flex items-center px-3 py-2.5 border-b border-border/15 transition-colors hover:bg-muted/30 ${
                isTop3 ? 'bg-primary/5' : isCut || isWD ? 'opacity-50' : ''
              }`}
              data-testid={`leaderboard-row-${i}`}
            >
              <span className={`w-8 text-center text-xs font-bold ${isTop3 ? 'text-primary' : 'text-muted-foreground'}`}>
                {isCut ? 'CUT' : isWD ? 'WD' : player.position}
              </span>
              <div className="flex-1 ml-2 min-w-0">
                <span className={`text-sm font-semibold truncate block ${isTop3 ? 'text-foreground' : 'text-foreground/80'}`}>
                  {player.short_name || player.name}
                </span>
                {player.country && (
                  <span className="text-[9px] text-muted-foreground">{player.country}</span>
                )}
              </div>
              <span className={`w-12 text-center text-sm font-black ${scoreColor(player.score_to_par)}`}>
                {formatScore(player.score_to_par)}
              </span>
              <span className="w-10 text-center text-[10px] text-muted-foreground font-medium">{player.thru}</span>
              {(player.rounds || []).map((r, j) => (
                <span key={j} className="w-8 text-center text-[10px] text-muted-foreground hidden sm:block">{r || '--'}</span>
              ))}
              {Array.from({ length: maxRounds - (player.rounds?.length || 0) }, (_, j) => (
                <span key={`e-${j}`} className="w-8 text-center text-[10px] text-muted-foreground hidden sm:block">--</span>
              ))}
            </div>
          );
        })}
      </div>
    );
  };

  const renderNews = () => {
    if (loadingNews) return <div className="flex items-center justify-center py-12 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading news...</div>;
    if (!news?.articles?.length) return <div className="text-center py-12 text-muted-foreground text-sm">No news available</div>;

    return (
      <div className="space-y-3">
        {news.articles.map((article, i) => (
          <a
            key={i}
            href={article.link}
            target="_blank"
            rel="noopener noreferrer"
            className="block bg-card rounded-xl border border-border/50 overflow-hidden hover:border-primary/30 transition-all group"
            data-testid={`news-article-${i}`}
          >
            <div className="flex gap-3 p-3">
              {article.image_url && (
                <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-muted">
                  <img src={article.image_url} alt="" className="w-full h-full object-cover" loading="lazy" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-bold text-foreground line-clamp-2 group-hover:text-primary transition-colors">{article.headline}</h4>
                {article.description && (
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{article.description}</p>
                )}
                <div className="flex items-center gap-2 mt-1.5">
                  {article.published && (
                    <span className="text-[9px] text-muted-foreground flex items-center gap-1">
                      <Calendar className="w-2.5 h-2.5" />
                      {new Date(article.published).toLocaleDateString()}
                    </span>
                  )}
                  <ExternalLink className="w-3 h-3 text-muted-foreground/50" />
                </div>
              </div>
            </div>
          </a>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background" data-testid="golf-updates-page">
      <div className="max-w-lg mx-auto px-4 pt-6 pb-24">
        {/* Header with tour picker */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="font-serif text-2xl font-bold" style={{ color: 'var(--deep-forest)' }}>Tour Updates</h1>
            <p className="text-sm mt-0.5" style={{ color: 'var(--warm-gray)' }}>Live leaderboards & news</p>
          </div>
        </div>

        {/* Tour Selector */}
        <div className="relative mb-4" data-testid="tour-selector">
          <button
            onClick={() => setShowTourPicker(!showTourPicker)}
            className="w-full flex items-center justify-between px-4 py-3 bg-card rounded-xl border border-border/50 hover:border-primary/30 transition-colors"
            data-testid="tour-picker-btn"
          >
            <span className="text-sm font-bold text-foreground">{tourLabel}</span>
            <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${showTourPicker ? 'rotate-180' : ''}`} />
          </button>
          {showTourPicker && (
            <div className="absolute left-0 right-0 top-full mt-1 z-30 bg-card border border-border/50 rounded-xl shadow-lg overflow-hidden" data-testid="tour-dropdown">
              {TOURS.map(t => (
                <button
                  key={t.value}
                  onClick={() => { setTour(t.value); setShowTourPicker(false); }}
                  className={`w-full flex items-center px-4 py-3 text-sm font-semibold transition-colors text-left ${
                    tour === t.value ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-muted/50'
                  }`}
                  data-testid={`tour-option-${t.value}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Leaderboard / News Tabs */}
        <div className="bg-card rounded-xl border border-border/50 p-1 flex gap-1 mb-4">
          <button
            onClick={() => setTab('leaderboard')}
            className={`flex-1 py-2.5 rounded-lg text-sm font-bold transition-all flex items-center justify-center gap-1.5 ${
              tab === 'leaderboard' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="tab-leaderboard"
          >
            <Trophy className="w-4 h-4" /> Leaderboard
          </button>
          <button
            onClick={() => setTab('news')}
            className={`flex-1 py-2.5 rounded-lg text-sm font-bold transition-all flex items-center justify-center gap-1.5 ${
              tab === 'news' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="tab-news"
          >
            <Newspaper className="w-4 h-4" /> News
          </button>
        </div>

        {/* Content */}
        {tab === 'leaderboard' && (
          <div>
            {renderTournamentHeader()}
            <div className="bg-card rounded-2xl border border-border/50 overflow-hidden" data-testid="leaderboard-table">
              {renderLeaderboard()}
            </div>
            {/* Other events this week */}
            {scoreboard?.other_events?.length > 0 && (
              <div className="mt-4">
                <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">Also This Week</h3>
                <div className="space-y-2">
                  {scoreboard.other_events.map(e => (
                    <div key={e.id} className="bg-card rounded-xl border border-border/50 p-3 text-sm text-foreground font-medium">{e.name}</div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {tab === 'news' && renderNews()}

        {/* Refresh */}
        <div className="mt-4 text-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { fetchScoreboard(tour); fetchNews(tour); }}
            className="text-xs text-muted-foreground"
            data-testid="refresh-updates"
          >
            Refresh
          </Button>
        </div>
      </div>
      <TooltipGuide pageKey="updates" />
      <Navigation />
    </div>
  );
};

export default GolfUpdates;
